import type { CSSProperties, MouseEvent } from 'react';
import React from 'react';
import { StyleSheet, View } from 'react-native';

import { STORE_LOCATOR_FOCUS_RESULT_ATTR } from '@fhs-legacy/frontend/src/pages/store-locator/store-locator-focus-handoff';

import type { StoreCardSelectRegionProps } from './store-card-select-region.types';

/**
 * A real `<button>` rather than a Pressable so the card keeps working with the keyboard tab
 * order, the address-search focus handoff, and `aria-current`. The visual content is hidden
 * from assistive tech and replaced by a single label, which keeps the announcement short and
 * lets the `(i)`, favourite, and order controls sit outside the button as real siblings.
 * Pointer clicks on the rest of the card are handled by the frame so the whole row selects.
 */
const buttonReset: CSSProperties = {
  appearance: 'none',
  WebkitAppearance: 'none',
  backgroundColor: 'transparent',
  border: 'none',
  color: 'inherit',
  cursor: 'pointer',
  display: 'block',
  font: 'inherit',
  margin: 0,
  minWidth: 0,
  padding: 0,
  textAlign: 'inherit',
  width: '100%',
};

export const StoreCardSelectRegion: React.VFC<StoreCardSelectRegionProps> = ({
  children,
  accessibilityLabel,
  isSelected,
  onPress,
  isStoreLocatorFocusTarget,
  storeLocatorFocusRef,
  'dd-action-name': ddActionName,
}) => {
  const onClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onPress?.();
  };

  return (
    <button
      ref={storeLocatorFocusRef}
      type="button"
      aria-label={accessibilityLabel}
      aria-current={isSelected ? 'true' : undefined}
      {...(isStoreLocatorFocusTarget ? { [STORE_LOCATOR_FOCUS_RESULT_ATTR]: 'true' } : {})}
      data-dd-action-name={ddActionName}
      onClick={onClick}
      style={buttonReset}
    >
      <View
        accessibilityElementsHidden
        importantForAccessibility="no-hide-descendants"
        style={styles.content}
        aria-hidden
      >
        {children}
      </View>
    </button>
  );
};

const styles = StyleSheet.create({
  content: {
    width: '100%',
  },
});
